import type { SVGProps } from 'react'

/**
 * Thin-line icons, 24×24, drawn with a 1.5px stroke in currentColor.
 * Kept in one file so the whole set reads as one hand.
 */
const GLYPHS = {
  projects: (
    <>
      <rect x="4" y="4" width="7" height="7" rx="2" />
      <rect x="13" y="4" width="7" height="7" rx="2" />
      <rect x="4" y="13" width="7" height="7" rx="2" />
      <rect x="13" y="13" width="7" height="7" rx="2" />
    </>
  ),
  equipment: (
    <>
      <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" />
      <path d="M12 12l8-4.5M12 12L4 7.5M12 12v9" />
    </>
  ),
  inventory: (
    <>
      <path d="M3.5 5.5A1.5 1.5 0 0 1 5 4h14a1.5 1.5 0 0 1 1.5 1.5V8h-17V5.5Z" />
      <path d="M5 8v10.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V8M10 12h4" />
    </>
  ),
  settings: (
    <>
      <path d="M4 7h8m4 0h4M4 17h4m4 0h8" />
      <circle cx="14" cy="7" r="2" />
      <circle cx="10" cy="17" r="2" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  logout: <path d="M10 4H6.5A1.5 1.5 0 0 0 5 5.5v13A1.5 1.5 0 0 0 6.5 20H10M15 8l4 4-4 4M19 12H9" />,
  plus: <path d="M12 5v14M5 12h14" />,
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M20 20l-3.5-3.5" />
    </>
  ),
  trash: <path d="M6 7h12M9 7V5h6v2m-8 0 1 12h8l1-12" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
  back: <path d="M19 12H5m7-7-7 7 7 7" />,
  arrowRight: <path d="M5 12h14m-6-6 6 6-6 6" />,
  split: <path d="M3 12h6l5-6h7M9 12l5 6h7" />,
  chevronDown: <path d="m6 9 6 6 6-6" />,
  chevronRight: <path d="m9 6 6 6-6 6" />,
  chevronLeft: <path d="m15 6-6 6 6 6" />,
  check: <path d="m5 12 5 5 9-10" />,
  download: <path d="M12 4v11m0 0 4-4m-4 4-4-4M5 19h14" />,
  print: (
    <>
      <path d="M7 8V4h10v4" />
      <path d="M6 17H5a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-1" />
      <path d="M7 14h10v6H7z" />
    </>
  ),
  sheet: <path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" />,
  image: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2.5" />
      <path d="M4 16l4.5-4.5L13 16l3-3 4 4M15.5 9h.01" />
    </>
  ),
  copy: (
    <>
      <path d="M8 8V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2" />
      <rect x="4" y="8" width="12" height="12" rx="2" />
    </>
  ),
  external: <path d="M14 5h5v5M19 5l-8 8M17 13v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h5" />,
  sparkle: (
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3zM19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8L19 16z" />
  ),
  save: <path d="M5 4h11l3 3v13H5V4zM8 4v5h7V4M8 20v-6h8v6" />,
  collapse: <path d="M4 14h6v6M20 10h-6V4M10 14l-6 6M14 10l6-6" />,
  cable: (
    <>
      <path d="M4 7h4a3 3 0 0 1 3 3v4a3 3 0 0 0 3 3h6" />
      <circle cx="4" cy="7" r="1.5" />
      <circle cx="20" cy="17" r="1.5" />
    </>
  ),
  keyboard: (
    <>
      <rect x="3" y="6" width="18" height="12" rx="2.5" />
      <path d="M7 10h.01M11 10h.01M15 10h.01M7 14h10" />
    </>
  ),
  camera: (
    <>
      <path d="M4 8.5A1.5 1.5 0 0 1 5.5 7h2l1.5-2h6l1.5 2h2A1.5 1.5 0 0 1 20 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5v-9Z" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ),
  chip: (
    <>
      <rect x="7" y="7" width="10" height="10" rx="2" />
      <path d="M10 10h4v4h-4zM9 3v4M15 3v4M9 17v4M15 17v4M3 9h4M3 15h4M17 9h4M17 15h4" />
    </>
  ),
  monitor: (
    <>
      <rect x="3" y="5" width="18" height="12" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </>
  ),
  wave: <path d="M3 12h2l2-5 3 10 3-8 2 5 2-2h4" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  network: (
    <>
      <rect x="9" y="3" width="6" height="5" rx="1.5" />
      <rect x="3" y="16" width="6" height="5" rx="1.5" />
      <rect x="15" y="16" width="6" height="5" rx="1.5" />
      <path d="M12 8v4M6 16v-2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2" />
    </>
  ),
  plug: <path d="M9 3v5M15 3v5M6 8h12v3a6 6 0 0 1-12 0V8ZM12 17v4" />,
  computer: (
    <>
      <rect x="6" y="3" width="12" height="18" rx="2" />
      <path d="M9 7h6M9 10h6M12 16h.01" />
    </>
  ),
  headset: (
    <>
      <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
      <rect x="3" y="13" width="4" height="6" rx="1.5" />
      <rect x="17" y="13" width="4" height="6" rx="1.5" />
      <path d="M19 19v1a2 2 0 0 1-2 2h-4" />
    </>
  ),
  broadcast: (
    <>
      <circle cx="12" cy="12" r="2.5" />
      <path d="M7.8 16.2a6 6 0 0 1 0-8.4M16.2 7.8a6 6 0 0 1 0 8.4M4.9 19.1a10 10 0 0 1 0-14.2M19.1 4.9a10 10 0 0 1 0 14.2" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  eyeOff: (
    <path d="M3 3l18 18M10.6 6.1A9.8 9.8 0 0 1 12 6c6 0 9.5 6 9.5 6a16 16 0 0 1-3.2 3.9M6.7 6.7C4 8.6 2.5 12 2.5 12s3.5 6 9.5 6a9.6 9.6 0 0 0 3.9-.8M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  ),
  star: (
    <path d="m12 3.5 2.6 5.4 5.9.8-4.3 4.1 1.1 5.9L12 16.9l-5.3 2.8 1.1-5.9-4.3-4.1 5.9-.8L12 3.5z" />
  ),
  starFilled: (
    <path
      fill="currentColor"
      d="m12 3.5 2.6 5.4 5.9.8-4.3 4.1 1.1 5.9L12 16.9l-5.3 2.8 1.1-5.9-4.3-4.1 5.9-.8L12 3.5z"
    />
  ),
  density: <path d="M4 6h16M4 10h16M4 14h16M4 18h16" />,
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>
  ),
  moon: <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />,
  upload: <path d="M12 16V5m0 0-4 4m4-4 4 4M5 19h14" />,
  // Equipment categories — brand/03-icons/equipment, supplied as 24×24 at
  // stroke 1.8; they take the set's 1.5 here so they sit with the rest.
  eqAudio: (
    <>
      <path d="M5 5v14M12 5v14M19 5v14"/>
      <path d="M3.5 9h3M10.5 15h3M17.5 11h3"/>
    </>
  ),
  eqCamera: (
    <>
      <path d="M3 8.5h12.5A2.5 2.5 0 0 1 18 11v6a2.5 2.5 0 0 1-2.5 2.5H3z"/>
      <path d="m18 12 3-2v8l-3-2"/>
      <circle cx="10.5" cy="14" r="2.6"/>
    </>
  ),
  eqCaptureCard: (
    <>
      <path d="M7 4h10v16H7zM10 4v3h4V4M7 9h10"/>
      <path d="M4 10v1M4 13v1M4 16v1M20 10v1M20 13v1M20 16v1"/>
    </>
  ),
  eqComputer: (
    <>
      <rect x="3" y="4" width="12" height="9" rx="2"/>
      <path d="M9 13v3M6 16h6"/>
      <rect x="17" y="4" width="4" height="15" rx="1"/>
      <path d="M19 15v1"/>
    </>
  ),
  eqConverter: (
    <>
      <rect x="3" y="6" width="18" height="12" rx="2"/>
      <path d="M7 10h7M12 8l2 2-2 2M17 14h-7M12 12l-2 2 2 2"/>
    </>
  ),
  eqEncoder: (
    <>
      <rect x="3" y="7" width="11" height="10" rx="2"/>
      <path d="M6 13v1M8.5 10.5v3.5M11 12v2"/>
      <path d="M17 9a4 4 0 0 1 0 6M20 7a7 7 0 0 1 0 10"/>
    </>
  ),
  eqIntercom: (
    <>
      <path d="M5 13v-1a7 7 0 0 1 14 0v1"/>
      <path d="M5 13h2v4H5zM17 13h2v4h-2zM17 17a4 4 0 0 1-4 3h-2"/>
    </>
  ),
  eqMonitor: (
    <>
      <rect x="3" y="4" width="18" height="12" rx="2"/>
      <path d="M12 16v4M8 20h8"/>
    </>
  ),
  eqMultiviewer: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2"/>
      <path d="M12 5v14M3 12h18"/>
    </>
  ),
  eqNetwork: (
    <>
      <circle cx="6" cy="7" r="2"/><circle cx="18" cy="7" r="2"/><circle cx="12" cy="17" r="2"/>
      <path d="m7.7 8.1 2.8 6.2M16.3 8.1l-2.8 6.2M8 7h8"/>
    </>
  ),
  eqOther: (
    <>
      <circle cx="6" cy="12" r="1" fill="currentColor"/><circle cx="12" cy="12" r="1" fill="currentColor"/><circle cx="18" cy="12" r="1" fill="currentColor"/>
    </>
  ),
  eqPower: (
    <>
      <path d="M9 4v5M15 4v5M7 9h10v2a5 5 0 0 1-10 0zM12 16v4"/>
    </>
  ),
  eqRecorder: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2"/>
      <circle cx="8" cy="12" r="1.5"/>
      <path d="m13 9 4 3-4 3z"/>
    </>
  ),
  eqRouter: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2"/>
      <path d="M6 9h2.5a2 2 0 0 1 2 2v2a2 2 0 0 0 2 2H18M6 15h2.5a2 2 0 0 0 2-2v-2a2 2 0 0 1 2-2H18"/>
    </>
  ),
  eqSwitcher: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2"/>
      <path d="M6 9h2M11 9h2M16 9h2M6 14h2M11 14h2M16 14h2"/>
    </>
  ),
  eqSync: (
    <>
      <circle cx="12" cy="12" r="8"/>
      <path d="M12 8v4l3 2M4 12h2M18 12h2"/>
    </>
  ),
} as const

export type IconName = keyof typeof GLYPHS

/**
 * Decoration by default, but a caller that supplies a label means it: the
 * category glyph on a canvas node, in the inspector and on the run sheet is
 * the only thing telling one box from the next. `aria-hidden` used to be
 * hard-coded ahead of `{...rest}`, which carries the label but not the hidden
 * flag, so every one of those labels was inert.
 */
export function Icon({
  name,
  className = 'h-5 w-5',
  strokeWidth = 1.5,
  ...rest
}: { name: IconName; className?: string; strokeWidth?: number } & Omit<SVGProps<SVGSVGElement>, 'name'>) {
  const labelled = 'aria-label' in rest || 'aria-labelledby' in rest
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={labelled ? 'img' : undefined}
      aria-hidden={labelled ? undefined : true}
      className={className}
      {...rest}
    >
      {GLYPHS[name]}
    </svg>
  )
}
